import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF, Sparkles } from '@react-three/drei';
import { RigidBody } from '@react-three/rapier';
import * as THREE from 'three';

import { useStore } from '../../store/useStore';
import { isMobile } from '../../utils/isMobile';

export const Fireplace = ({ position = [0, 0, 0] }: { position?: [number, number, number] }) => {
    const { scene } = useGLTF('/assets/Blender/GLB/campfire.glb');
    const modelRef = useRef<THREE.Group>(null);
    const lightRef = useRef<THREE.PointLight>(null);
    const theme = useStore(state => state.theme);
    const isCpp = theme === 'cyberpunk';

    const prepared = useMemo(() => {
        const clone = scene.clone(true);
        const scale = 2.0; // Increased scale
        clone.scale.setScalar(scale);
        clone.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });
        
        // Manual bounding box calculation to avoid Box3.setFromObject glitches
        const box = new THREE.Box3();
        clone.updateWorldMatrix(true, true);
        let hasGeometry = false;
        
        clone.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.isMesh && mesh.geometry) {
                mesh.geometry.computeBoundingBox();
                if (mesh.geometry.boundingBox) {
                    const bb = mesh.geometry.boundingBox.clone();
                    bb.applyMatrix4(mesh.matrixWorld);
                    box.union(bb);
                    hasGeometry = true;
                }
            }
        });

        if (hasGeometry) {
             const yOffset = -box.min.y;
             clone.position.y += yOffset;
        }

        return clone;
    }, [scene]);

    useFrame((state) => {
        if (lightRef.current) {
            // Smooth flicker effect (clamped) to avoid extreme flashes that can trigger visual jumps
            const time = state.clock.elapsedTime;
            const flicker = Math.sin(time * 4) * 0.25 + Math.sin(time * 7) * 0.1;
            lightRef.current.intensity = THREE.MathUtils.clamp(1.6 + flicker, 0.8, 2.5);
            lightRef.current.position.y = 1 + Math.sin(time * 2) * 0.05;
        }
    });

    return (
        <group position={position}>
            {/* Campfire GLB Model */}
            <RigidBody type="fixed" colliders="hull">
                <primitive
                    ref={modelRef}
                    object={prepared}
                />
            </RigidBody>

            {/* Fire Light */}
            <pointLight ref={lightRef} position={[0, 1, 0]} color={isCpp ? "#00ffff" : "#ff6600"} distance={10} castShadow />

            {/* Particles (Use 'drei' Sparkles for ease) */}
            {/* Particles: reduce counts on mobile to avoid GPU spikes */}
            <Sparkles
                count={isMobile() ? 18 : 50}
                scale={[1.5, 3, 1.5]}
                size={isMobile() ? 3 : 6}
                speed={isMobile() ? 0.25 : 0.4}
                opacity={0.8}
                color={isCpp ? '#00ffff' : '#ffaa00'}
                position={[0, 1.5, 0]}
            />
            {/* Some smoke/darker particles */}
            <Sparkles
                count={isMobile() ? 10 : 30}
                scale={[2, 4, 2]}
                size={isMobile() ? 6 : 10}
                speed={isMobile() ? 0.12 : 0.2}
                opacity={0.3}
                color={isCpp ? '#ff00ff' : '#444'}
                position={[0, 3, 0]}
                noise={1}
            />
        </group>
    );
};

useGLTF.preload('/assets/Blender/GLB/campfire.glb');
